import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { OrderStatus, OrderType } from "../../types";
import { useUpdateOrderStatusMutation } from "../../store";
import { IoChevronDown } from "react-icons/io5";
import { showToast, ToastType } from "../../utils/toast.utils";
import { orderStatusColors, OrderStatusLabels } from "../../configs";

interface OrderStatusSelectProps {
  orderId: string;
  value: OrderStatus;
  orderType: OrderType;
  className?: string;
}

const OrderStatusSelect: React.FC<OrderStatusSelectProps> = ({
  orderId,
  value,
  orderType,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  const deliveryStatuses = [
    OrderStatus.WAITING_FOR_PAYMENT,
    OrderStatus.PROCESSING,
    OrderStatus.COOKING,
    OrderStatus.WAITING_FOR_RIDER,
    OrderStatus.DRIVER_ASSIGNED,
    OrderStatus.COLLECTED,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELLED,
  ];

  const pickupStatuses = [
    OrderStatus.WAITING_FOR_PAYMENT,
    OrderStatus.PROCESSING,
    OrderStatus.COOKING,
    OrderStatus.WAITING_FOR_PICKUP,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELLED,
  ];

  const availableStatuses =
    orderType === OrderType.DELIVERY ? deliveryStatuses : pickupStatuses;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Set dropdown position
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyles({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 200),
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  const handleSelect = async (newStatus: OrderStatus) => {
    if (newStatus === value) {
      setIsOpen(false);
      return;
    }
    setIsUpdating(true);
    const result = await updateOrderStatus({ orderId, status: newStatus });
    if (!result.error) {
      showToast("Status mis à jour", ToastType.SUCCESS);
    }
    setIsUpdating(false);
    setIsOpen(false);
  };

  const wrapperClasses = classNames("relative flex flex-col z-10", className);

  const currentBgColor = orderStatusColors[value] || "bg-white";

  const inputContainerClasses = twMerge(
    classNames(
      "w-full flex justify-between items-center p-2 border rounded cursor-pointer",
      isOpen ? "ring-2 ring-primary" : "border-gray-300",
      isUpdating ? "opacity-50 cursor-not-allowed" : "",
      currentBgColor,
      "text-white"
    )
  );

  return (
    <div
      ref={containerRef}
      className={wrapperClasses}
    >
      <div
        ref={inputRef}
        className={inputContainerClasses}
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
      >
        <span>{OrderStatusLabels[value]}</span>
        <IoChevronDown
          className={classNames("text-white transition-transform", {
            "rotate-180": isOpen,
          })}
          size={16}
        />
      </div>

      {isOpen &&
        createPortal(
          <div
            style={dropdownStyles}
            className="border shadow-xl rounded max-h-60 overflow-y-auto"
          >
            {availableStatuses.map((key) => {
              const bgColor =
                orderStatusColors[key as OrderStatus] || "bg-white";
              const label = OrderStatusLabels[key as OrderStatus];
              return (
                <div
                  key={key}
                  className={twMerge(
                    `p-2 cursor-pointer hover:brightness-90 ${bgColor} text-white`
                  )}
                  onMouseDown={() => handleSelect(key as OrderStatus)}
                >
                  {label}
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default OrderStatusSelect;
